import { useCallback, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { AxiosError } from "axios"
import { useTranslation } from "react-i18next"

import { getErrorMessages } from "$/utils/get-error-messages"

import {
    Button,
    ETypographyAlignment,
    ETypographyColor,
    ETypographyFamily,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    Typography
} from "@/3514/components"
import { useSimulabContext } from "@/entities/simulab"
import { useEndConversation } from "@/entities/simulab/api"
import { QueryKey } from "@/entities/simulab/constants"
import { WarningMessage } from "@/shared/WarningMessage"
import Modal from "@/shared/modal"

import "../styles.scss"

export const SimulabEndConversationModal = () => {
    const queryClient = useQueryClient()
    const { t } = useTranslation()
    const { showEndConversationModal, conversation, setShowEndConversationModal } = useSimulabContext()
    const [error, setError] = useState(null)
    const endConversation = useEndConversation()

    const onEndConversationClick = useCallback(() => {
        endConversation.mutate(
            { conversationId: `${conversation?.id}` },
            {
                onSuccess() {
                    queryClient.invalidateQueries({
                        queryKey: [QueryKey.SimulabConversation, { id: conversation?.id }]
                    })
                    setShowEndConversationModal(false)
                },
                onError(error: AxiosError) {
                    setError(getErrorMessages(error))
                }
            }
        )
    }, [conversation, setShowEndConversationModal, endConversation])

    const onCloseModal = useCallback(() => {
        setShowEndConversationModal(false)
    }, [setShowEndConversationModal])

    return (
        <Modal
            show={showEndConversationModal && conversation.status !== "completed"}
            onClose={onCloseModal}
            modalBodyClass="simulab-end-conversation-modal"
            headerContent={
                <Typography
                    text={t("Congrats, You Finished D.I.G.!")}
                    className="p-1 m-0"
                    color={ETypographyColor.Black}
                    weight={ETypographyWeight.Medium}
                    family={ETypographyFamily.DmSerif}
                    alignment={ETypographyAlignment.Center}
                    size={ETypographySize.Heading2}
                    tag={ETypographyTag.Heading2}
                />
            }
        >
            <section className="simulab-end-conversation-modal__body">
                <p className="font-extrabold text-gray-100 mb-[20px] text-center">
                    {t("We detected a ‘Plan’ Question. Are you done ‘Digging’?")}
                </p>
                <p className="mb-[30px] text-gray-100 text-center">
                    {t(
                        "The SimuLab focuses exclusively on D.I.G., since that’s where we see the greatest opportunity to stimulate coachee growth (and encounter new challenges!). Ending the conversation will generate a feedback summary that provides analysis of your strengths and growth areas."
                    )}
                </p>
                <WarningMessage message={error} />
                <div className="mx-auto w-max mb-1">
                    <Button onClick={onEndConversationClick}>{t("Yes, End Conversation")}</Button>
                </div>
                <div className="mx-auto w-max">
                    <Button isContainer onClick={onCloseModal}>
                        <span className="text-accent fs-14">{t("No, I want to ask another D.I.G. question")}</span>
                    </Button>
                </div>
            </section>
        </Modal>
    )
}
